import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import './Header.css';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // If at the very top of the page (within 50px), select only "Home"
      if (window.scrollY <= 50) {
        setActiveSection('home');
        return;
      }
      
      // Scroll spy functionality
      const sections = ['about', 'services', 'contact'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    
    if (sectionId === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveSection('home'); // Set Home as active when going to top
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 100; // Account for header height
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  const navItems = [
    { name: 'Home', action: () => scrollToSection('home'), section: 'home' },
    { name: 'About Us', action: () => scrollToSection('about'), section: 'about' },
    { name: 'Services', action: () => scrollToSection('services'), section: 'services' },
    { name: 'Contact', action: () => scrollToSection('contact'), section: 'contact' }
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="contact-info">
              <span><Phone size={16} /> +91 8748863377</span>
              <span><Phone size={16} /> +91 7204273201</span>
              <span><Mail size={16} /> hr@futuretechhrservices.com</span>
            </div>
            <div className="company-info">
              <span>Est. 2013 | CIN: U78100KA2025PTC208816</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <motion.div
              className="logo"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/">
                <img 
                  src="/logo.png" 
                  alt="Future Tech HR Services" 
                  className="logo-image"
                />
                <div className="logo-text">
                  <h1>Future Tech HR</h1>
                  <span>Services Private Limited</span>
                </div>
              </Link>
            </motion.div>

            <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
              <ul className="nav-list">
                {navItems.map((item, index) => (
                  <motion.li
                    key={item.name}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <button
                      onClick={item.action}
                      className={activeSection === item.section ? 'active' : ''}
                    >
                      {item.name}
                    </button>
                  </motion.li>
                ))}
              </ul>
            </nav>

            <motion.div
              className="header-actions"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header actions removed */}
            </motion.div>

            <button
              className="mobile-menu-btn"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
